import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { useEffect, useState } from 'react';
import { feedback } from 'react-feedbacker';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';

import { useFeedbackMessages } from '@/bits';
import {
  Button,
  Card,
  CardCloseButton,
  CardOptions,
  ConfirmButton,
  ErrorMessage,
  NakedForm,
  SubmitButton,
  Table,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { FundingOption } from '@/globalTypes';
import { useIsMounted } from '@/hooks';
import { Nullable } from '@/types';
import {
  PlayerRequestDocumentsFormQuery,
  PlayerRequestDocumentsFormQueryVariables,
  RequestDocumentsMutation,
  RequestDocumentsMutationVariables,
} from './__generated__/PlayerRequestDocumentsForm';
import { AddDocumentSection } from './AddDocumentSection';
import { useFundingOptions } from './useFundingOptions';

const STATIC_QUERY = graphql`
  query SanityPlayerRequestDocumentsForm {
    sanityPlayerRequestDocumentsForm {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const query = gql`
  query PlayerRequestDocumentsForm($playerId: ID!) {
    player(playerId: $playerId) {
      id
      rawPlayerId
      playerComplianceDetails: playerComplianceDetailsV3 {
        id
        requiredDocumentTypes {
          requestedFundingType
          comment
        }
      }
    }
  }
`;

const requestDocumentsMutation = gql`
  mutation RequestDocuments(
    $playerId: ID!
    $requestedSowDocuments: [RequestedFundingType!]!
  ) {
    requestDocuments: requestDocumentsV3(
      playerId: $playerId
      requestedSowDocumentsV2: $requestedSowDocuments
    ) {
      id
      requiredDocumentTypes {
        requestedFundingType
        comment
      }
    }
  }
`;

type FormValues = {
  requestedSowDocuments: FundingOption[];
};

export type DocumentWithComment = {
  document: FundingOption;
  comment: string;
};
type DocumentsTableProps = {
  documents: DocumentWithComment[];
  onRemoveDocument: (document: FundingOption) => void;
  getDocumentLabel: (document: FundingOption) => string;
};

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  documents,
  onRemoveDocument,
  getDocumentLabel,
}) => {
  if (documents.length === 0) {
    return (
      <p className="text-gray-500 text-sm text-center">
        No documents added yet.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table
        headings={['Document Type', 'Comment', 'Actions']}
        columnWidths={['30%', '60%', '10%']}
        rows={documents.map((item, index) => ({
          key: `${item.document}-${index}`,
          data: [
            getDocumentLabel(item.document),
            item.comment || '-',
            <Button
              key="remove"
              type="button"
              onClick={() => onRemoveDocument(item.document)}
            >
              Remove
            </Button>,
          ],
        }))}
      />
    </div>
  );
};

const PlayerRequestDocumentsForm = ({ playerId }: { playerId: string }) => {
  const { t } = useTranslate();
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [listHasChanges, setListHasChanges] = useState<boolean>(false);

  const [documentsWithComments, setDocumentsWithComments] = useState<
    DocumentWithComment[]
  >([]);

  const isMounted = useIsMounted();
  const { close } = useModalContext();
  const feedbackMessages = useFeedbackMessages();

  const [{ data }] = useQuery<
    PlayerRequestDocumentsFormQuery,
    PlayerRequestDocumentsFormQueryVariables
  >({
    query,
    variables: { playerId },
  });

  const form =
    useStaticQuery<Queries.SanityPlayerRequestDocumentsFormQuery>(
      STATIC_QUERY,
    ).sanityPlayerRequestDocumentsForm;

  const [requestDocumentsState, requestDocuments] = useMutation<
    RequestDocumentsMutation,
    RequestDocumentsMutationVariables
  >(requestDocumentsMutation);

  const fundingOptions = useFundingOptions();

  const methods = useForm<FormValues>({
    defaultValues: {
      requestedSowDocuments: [],
    },
  });

  useEffect(() => {
    if (data?.player.playerComplianceDetails?.requiredDocumentTypes) {
      const initialDocuments: DocumentWithComment[] =
        data.player.playerComplianceDetails.requiredDocumentTypes.map(
          (doc) => ({
            document: doc.requestedFundingType,
            comment: doc.comment || '',
          }),
        );
      setDocumentsWithComments(initialDocuments);
    }
  }, [data]);

  const onAddDocument = (documentWithComment: DocumentWithComment) => {
    setDocumentsWithComments((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.document === documentWithComment.document,
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = documentWithComment;
        return updated;
      }

      return [...prev, documentWithComment];
    });

    setShowConfirmation(false);
    setListHasChanges(true);
  };

  const removeDocument = (documentToRemove: FundingOption) => {
    setDocumentsWithComments((prev) =>
      prev.filter((item) => item.document !== documentToRemove),
    );

    setShowConfirmation(false);
    setListHasChanges(true);
  };

  const onSubmit = () => {
    setErrorMessage(null);

    // Check if there are no documents and show confirmation
    if (documentsWithComments.length === 0 && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    const requestedDocuments = documentsWithComments.map((item) => ({
      requestedFundingType: item.document,
      comment: item.comment,
    }));

    requestDocuments({
      playerId,
      requestedSowDocuments: requestedDocuments,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }

      if (!res.error) {
        feedback.success(t(feedbackMessages.success));
      }
    });

    setShowConfirmation(false);
  };

  const getDocumentLabel = (document: FundingOption) => {
    const option = fundingOptions.find((opt) => opt.value === document);
    return option?.label || document;
  };

  if (!form || !data) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <div className="p-3">
        <NakedForm methods={methods} onSubmit={onSubmit} className="grid gap-6">
          <div className="space-y-3">
            <DocumentsTable
              documents={documentsWithComments}
              onRemoveDocument={removeDocument}
              getDocumentLabel={getDocumentLabel}
            />
          </div>
          <AddDocumentSection
            fundingOptions={fundingOptions}
            onAddDocument={onAddDocument}
          />
          <ErrorMessage message={errorMessage} />
          <p className="dark:text-gray-500 text-sm">
            WARNING: By submitting this form, you are going to request the above
            documents and their comments. The new changes will be applied only
            after submitting the request.
          </p>
          {documentsWithComments.length === 0 && showConfirmation ? (
            <div className="flex gap-3">
              <ConfirmButton
                type="button"
                onClick={onSubmit}
                disabled={requestDocumentsState.fetching}
                variant="primary"
              >
                Remove all previous requests?
              </ConfirmButton>
              <Button
                type="button"
                onClick={() => setShowConfirmation(false)}
                variant="secondary"
                disabled={requestDocumentsState.fetching}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <SubmitButton
              value="Submit Request"
              disabled={requestDocumentsState.fetching || !listHasChanges}
            />
          )}
        </NakedForm>
      </div>
    </Card>
  );
};

export default PlayerRequestDocumentsForm;
